import { useState } from 'react';
import { FormIcon, ReplyIcon, ThreadIcon, cn } from '@playerbase/ui';

import { ReactionPill } from './feedback-row.js';
import { SentimentDot } from './sentiment-dot.js';
import { BuildMarkerLabel } from '../games/build-marker-label.js';
import type { BuildMarkerSummary } from '../games/build-timeline.js';
import type { FormSubmissionEntry } from './use-feedback-inbox.js';
import type {
  FormSubmissionEngagement,
  SubmissionReply,
} from '../games/feedback-forms-data.js';

// ---------------------------------------------------------------------------
// Form submission card.
//
// Renders one feedback_form_submissions row. Header carries the form
// title, submitter (or "Anonymous"), time-ago and a channel chip. Body
// renders one Q/A block per field, ordered by the form's orderIndex.
//
// Submissions don't bundle, don't carry reactions, and aren't editable -
// the structure is intentionally thinner than poll-group-card or the
// chronological feed row. Reuse here would only buy us deep-tree noise.
// ---------------------------------------------------------------------------

interface FormSubmissionCardProps {
  entry: FormSubmissionEntry;
  channelLabel: string | null;
  // Build live when the form was submitted, joined client-side from the
  // game's marker timeline (see buildActiveAt). Resolved by the caller so
  // the card stays presentational. undefined hides the row entirely (the
  // caller hasn't wired build context); null renders "Unregistered build".
  buildMarker?: BuildMarkerSummary | null;
  // Compact view truncates long answers at 200 chars with a "Show more"
  // toggle (inbox pane). Expanded view (per-form responses page) drops
  // the truncation so the studio sees the full answer at a glance.
  expanded?: boolean;
  highlighted?: boolean;
  innerRef?: ((el: HTMLDivElement | null) => void) | undefined;
}

const ONE_MINUTE_MS = 60_000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const ANSWER_TRUNCATE_AT = 200;

function formatRelativePast(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  if (diff < ONE_MINUTE_MS) return 'just now';
  if (diff < ONE_HOUR_MS) return `${Math.floor(diff / ONE_MINUTE_MS)}m ago`;
  if (diff < ONE_DAY_MS) return `${Math.floor(diff / ONE_HOUR_MS)}h ago`;
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatAnswer(
  value: string | number | undefined,
  field: { kind?: string; config?: Record<string, unknown> } | undefined,
): string {
  if (value === undefined) return '—';
  if (field?.kind === 'binary') {
    // Stored value is canonical 'true' / 'false'; resolve to the
    // studio's current labels. Fall through for legacy rows that
    // recorded the literal label.
    const v = String(value).toLowerCase();
    if (v === 'true' || v === 'false') {
      const config = field.config ?? {};
      const trueRaw = config['trueLabel'];
      const falseRaw = config['falseLabel'];
      const trueLabel =
        typeof trueRaw === 'string' && trueRaw.length > 0 ? trueRaw : 'Yes';
      const falseLabel =
        typeof falseRaw === 'string' && falseRaw.length > 0 ? falseRaw : 'No';
      return v === 'true' ? trueLabel : falseLabel;
    }
  }
  if (typeof value === 'number') return String(value);
  if (value.trim() === '') return '—';
  return value;
}

interface AnswerBlockProps {
  question: string;
  answer: string;
  expanded: boolean;
}

function AnswerBlock({ question, answer, expanded }: AnswerBlockProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const overflow = !expanded && answer.length > ANSWER_TRUNCATE_AT;
  const visible =
    overflow && !open ? `${answer.slice(0, ANSWER_TRUNCATE_AT).trimEnd()}…` : answer;
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-medium text-text-muted">Q: {question}</p>
      <p className="whitespace-pre-wrap break-words text-sm text-text">A: {visible}</p>
      {overflow && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-medium text-accent hover:underline"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function ReplyLine({ reply }: { reply: SubmissionReply }): JSX.Element {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="font-medium text-text">
        {reply.authorHandle ?? 'Unknown'}
      </span>
      <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-text-muted">
        {reply.rawText}
      </p>
    </div>
  );
}

function EngagementBar({
  engagement,
}: {
  engagement: FormSubmissionEngagement;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const hasReactions = engagement.reactions.length > 0;
  const hasReplies = engagement.replyCount > 0;
  const hasThread = engagement.threadReplyCount > 0;
  if (!hasReactions && !hasReplies && !hasThread) return null;
  return (
    <div className="space-y-2 border-t border-border pt-2">
      {hasReactions && (
        <div className="flex flex-wrap items-center gap-1">
          {engagement.reactions.map((r) => (
            <ReactionPill key={r.identifier} reaction={r} />
          ))}
        </div>
      )}
      {(hasReplies || hasThread) && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-3 text-xs text-text-muted hover:text-text"
        >
          {hasReplies && (
            <span className="inline-flex items-center gap-1">
              <ReplyIcon className="h-3.5 w-3.5" />
              <span className="tabular-nums">{engagement.replyCount}</span>
            </span>
          )}
          {hasThread && (
            <span className="inline-flex items-center gap-1">
              <ThreadIcon className="h-3.5 w-3.5" />
              <span className="tabular-nums">{engagement.threadReplyCount}</span>
            </span>
          )}
          <span className="ml-auto font-medium text-accent">
            {open ? 'Hide' : 'Show'}
          </span>
        </button>
      )}
      {open && (
        <div className="space-y-2 pl-1">
          {engagement.replies.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">
                Replies
              </p>
              {engagement.replies.map((r) => (
                <ReplyLine key={r.id} reply={r} />
              ))}
            </div>
          )}
          {engagement.threadReplies.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">
                Thread
              </p>
              {engagement.threadReplies.map((r) => (
                <ReplyLine key={r.id} reply={r} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FormSubmissionCard({
  entry,
  channelLabel,
  buildMarker,
  expanded = false,
  highlighted = false,
  innerRef,
}: FormSubmissionCardProps): JSX.Element {
  const submitter = entry.submitterHandle ?? 'Anonymous';
  const isAnonymous = entry.submitterHandle === null;
  return (
    <div
      ref={innerRef}
      className={cn(
        'transition-colors',
        highlighted && 'bg-info-bg ring-2 ring-info ring-inset',
      )}
    >
      <div className="space-y-3 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="flex items-center gap-1.5 text-sm font-medium text-text">
              <FormIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">{entry.formTitle}</span>
              <SentimentDot band={entry.sentimentBand} />
            </h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-muted">
              <span className={cn('truncate', isAnonymous && 'italic')}>{submitter}</span>
              {channelLabel !== null && (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">#{channelLabel}</span>
                </>
              )}
              {isAnonymous && (
                <span className="rounded-sm border border-border bg-surface-2 px-1 py-0.5 font-mono text-[10px] uppercase tracking-wider">
                  Anon
                </span>
              )}
              {buildMarker !== undefined && (
                <>
                  <span aria-hidden>·</span>
                  <BuildMarkerLabel marker={buildMarker} />
                </>
              )}
            </div>
          </div>
          <span className="shrink-0 whitespace-nowrap text-xs text-text-muted">
            {formatRelativePast(entry.submittedAt)}
          </span>
        </div>
        {entry.fields.length === 0 ? (
          <p className="text-xs text-text-muted">No fields recorded.</p>
        ) : (
          <div className="space-y-2.5">
            {entry.fields.map((field) => (
              <AnswerBlock
                key={field.id}
                question={field.title}
                answer={formatAnswer(entry.values[field.id], field)}
                expanded={expanded}
              />
            ))}
          </div>
        )}
        {entry.engagement !== null && <EngagementBar engagement={entry.engagement} />}
      </div>
    </div>
  );
}
